import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withKeys as _withKeys, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "relative my-4 h-10" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" }, [
  /*#__PURE__*/_createElementVNode("svg", {
    class: "h-5 w-5 text-gray-400",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 20 20",
    fill: "currentColor",
    "aria-hidden": "true"
  }, [
    /*#__PURE__*/_createElementVNode("path", {
      "fill-rule": "evenodd",
      d: "M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z",
      "clip-rule": "evenodd"
    })
  ])
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("label", {
  for: "inputDomain",
  class: "sr-only"
}, "Domain name", -1)
const _hoisted_4 = ["onKeydown", "value", "placeholder"]
const _hoisted_5 = { class: "grow-0 shrink-0" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["onClick", "onMouseover", "onFocus"]
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "text-gray-800" }

import axios, { AxiosResponse, Canceler } from 'axios';
import {
  ref, computed, onMounted, watch,
} from 'vue';

type AutocompleteDomain = {
  prefix: string;
  completion: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DomainSearch',
  props: {
  autofocus: Boolean,
  emphasize: Boolean,
  autocompleteEnabled: {
    type: Boolean,
    default: true,
  },
  prefill: {
    type: String,
    default: '',
  },
  buttonText: {
    type: String,
    default: 'Find DNS records',
  },
  placeholderText: {
    type: String,
    default: 'Domain name',
  },
},
  emits: ['submitted'],
  setup(__props, { emit }) {

const props = __props;





const autocompleteUrlPrefix = 'autocomplete/';

const autocompleteDomains = ref(new Array<AutocompleteDomain>());
const inputDomain = ref<HTMLInputElement | null>(null);
const cancelToken = ref<Canceler | null>(null);
const visibleInput = ref('');
const prefix = ref('');
const selected = ref(-1);
const hasFocus = ref(false);

// see https://github.com/vuejs/vue/issues/9299#issuecomment-481530279
function autocomplete(evt: Event): void {
  const target = evt.target as HTMLInputElement;
  prefix.value = target.value;
  visibleInput.value = target.value;

  if (!props.autocompleteEnabled) {
    return;
  }

  selected.value = -1;
  if (visibleInput.value === '') {
    autocompleteDomains.value = [];
  } else {
    if (cancelToken.value) {
      cancelToken.value();
    }
    const axiosSource = axios.CancelToken.source();
    cancelToken.value = axiosSource.cancel;
    axios.get(`${autocompleteUrlPrefix}${visibleInput.value}`, {
      cancelToken: axiosSource.token,
    }).then((response: AxiosResponse) => {
      const { url } = response.config;
      if (url) {
        const fetchedPrefix = url.substr(autocompleteUrlPrefix.length, url.length);
        // Prevent race conditions
        if (fetchedPrefix === prefix.value) {
          if (response.data === '') {
            autocompleteDomains.value = [];
          } else {
            autocompleteDomains.value = response.data.split('\n')
              .flatMap((result: string) => [{
                prefix: visibleInput.value,
                completion: result,
              }]);
          }
        }
      }
    }).catch((error) => {
      if (!axios.isCancel(error)) {
        console.log(error.message);
      }
    });
  }
}

function select(index: number, updateInput = false): void {
  if (updateInput) {
    visibleInput.value = autocompleteDomains.value[index].prefix + autocompleteDomains.value[index].completion;
  }
  selected.value = index;
}

function cancelSelection(): void {
  visibleInput.value = prefix.value;
  selected.value = -1;
}

function move(direction: number): boolean {
  if (selected.value === -1) {
    if (direction === -1) {
      select(autocompleteDomains.value.length - 1, true);
    } else {
      select(0, true);
    }
  } else {
    select((selected.value + autocompleteDomains.value.length + direction) % autocompleteDomains.value.length, true);
  }
  return false;
}

function redirectTo(index: number): void {
  const input = inputDomain.value as HTMLInputElement;
  input.blur();
  emit('submitted', autocompleteDomains.value[index].prefix + autocompleteDomains.value[index].completion);
}

function redirectToDnsRecords(): void {
  if (visibleInput.value !== '') {
    const input = inputDomain.value as HTMLInputElement;
    input.blur();
    emit('submitted', visibleInput.value);
  }
}

const autocompleting = computed(() => autocompleteDomains.value.length > 0 && hasFocus.value);

onMounted(() => {
  if (props.autofocus) {
    const input = inputDomain.value as HTMLInputElement;
    input.focus();
  }
});

watch(() => props.prefill, () => {
  visibleInput.value = props.prefill;
}, {
  immediate: true,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["flex border border-gray-300 bg-white rounded-md h-full overflow-hidden", { 'rounded-b-none': _unref(autocompleting), shadow: __props.emphasize }])
    }, [
      _hoisted_2,
      _hoisted_3,
      _createElementVNode("input", {
        class: "w-full min-w-0 pl-11 outline-none",
        onInput: autocomplete,
        onKeydown: [
          _cache[0] || (_cache[0] = _withKeys(_withModifiers(($event: any) => (move(-1)), ["prevent"]), ["up"])),
          _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (move(1)), ["prevent"]), ["down"])),
          _withKeys(cancelSelection, ["esc"])
        ],
        onFocus: _cache[2] || (_cache[2] = ($event: any) => (hasFocus.value = true)),
        onBlur: _cache[3] || (_cache[3] = ($event: any) => (hasFocus.value = false)),
        value: visibleInput.value,
        type: "url",
        id: "inputDomain",
        ref_key: "inputDomain",
        ref: inputDomain,
        placeholder: __props.placeholderText,
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        spellcheck: "false"
      }, null, 40, _hoisted_4),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          class: _normalizeClass(['h-full align-middle px-4 whitespace-nowrap border-gray-300 border-l',
                         __props.emphasize ? 'text-white bg-[#3a0161] hover:bg-[#23013c]' : 'text-gray-800 hover:text-white hover:bg-[#3a0161]']),
          type: "submit",
          onClick: _withModifiers(redirectToDnsRecords, ["stop","prevent"])
        }, _toDisplayString(__props.buttonText), 11, _hoisted_6)
      ])
    ], 2),
    _withDirectives(_createElementVNode("ul", {
      onMousedownCapture: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
      class: "absolute border-box p-0 w-full overflow-hidden z-[1001] text-left text-gray-500 bg-white border border-t-0 border-gray-300 rounded-b-md"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(autocompleteDomains.value, (autocompleteDomain, index) => {
        return (_openBlock(), _createElementBlock("li", {
          key: `${autocompleteDomain.prefix}${autocompleteDomain.completion}`,
          onClick: ($event: any) => (redirectTo(index)),
          onMouseover: ($event: any) => (select(index)),
          onFocus: ($event: any) => (select(index)),
          class: _normalizeClass(["px-3 py-1.5 list-none cursor-pointer", { 'bg-gray-200': index === selected.value }])
        }, [
          _createElementVNode("img", {
            class: "h-4 w-4 inline inset-y-0 left-0 mr-4",
            src: `https://icons.duckduckgo.com/ip3/${autocompleteDomain.prefix}${autocompleteDomain.completion}.ico`,
            alt: ""
          }, null, 8, _hoisted_8),
          _createTextVNode(_toDisplayString(autocompleteDomain.prefix), 1),
          _createElementVNode("span", _hoisted_9, _toDisplayString(autocompleteDomain.completion), 1)
        ], 42, _hoisted_7))
      }), 128))
    ], 544), [
      [_vShow, _unref(autocompleting)]
    ])
  ]))
}
}

})